import React from 'react';
import { Check, CheckCheck, AlertCircle, Clock } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { Tick } from '../readState';

export type ReceiptState = Tick | 'sending' | 'failed';

/**
 * The receipt indicator beside a timestamp.
 *
 * Only ever rendered for the sender's OWN messages — a recipient has no receipt
 * to show on a message they received, and putting one there is the classic way
 * this feature ends up confusing.
 *
 * Status is never colour alone. Blue-vs-grey is a reinforcement; the meaning
 * lives in the accessible label, so the state is readable to a screen reader
 * and to anyone who cannot distinguish the two greys.
 */
export const ReceiptTicks: React.FC<{
  state: ReceiptState;
  /** Timestamp of the read, when known — folded into the label, not shown. */
  readAt?: string;
  /** true inside an orange outgoing bubble, where grey would disappear. */
  onBubble?: boolean;
  t: TFunction;
  onRetry?: () => void;
}> = ({ state, readAt, onBubble, t, onRetry }) => {
  if (state === 'failed') {
    // The send queue already retries on reconnect, so when no explicit handler
    // is passed this is a STATUS, not a control. Rendering it as a button that
    // does nothing would be both a lie and an accessibility trap — the label
    // says what will actually happen instead.
    const autoLabel = t('receipts.failed_auto', {
      defaultValue: 'Not sent. It will retry automatically.',
    });
    if (!onRetry) {
      return (
        <span
          data-testid="receipt-failed"
          role="img"
          aria-label={autoLabel}
          title={autoLabel}
          className="ml-1 inline-flex items-center align-bottom text-red-300"
        >
          <AlertCircle size={13} aria-hidden="true" />
        </span>
      );
    }
    const retryLabel = t('receipts.failed', { defaultValue: 'Failed to send. Tap to retry.' });
    return (
      <button
        type="button"
        onClick={onRetry}
        data-testid="receipt-failed"
        aria-label={retryLabel}
        title={retryLabel}
        className="ml-1 inline-flex items-center align-bottom text-red-300 hover:text-red-200"
      >
        <AlertCircle size={13} aria-hidden="true" />
      </button>
    );
  }

  if (state === 'sending') {
    return (
      <span
        data-testid="receipt-sending"
        role="img"
        aria-label={t('receipts.sending', { defaultValue: 'Sending' })}
        className={`ml-1 inline-flex items-center align-bottom ${onBubble ? 'text-white/50' : 'text-muted-foreground'}`}
      >
        <Clock size={12} aria-hidden="true" />
      </span>
    );
  }

  // Grey on an orange bubble needs to be a translucent white or it vanishes;
  // blue has to stay recognisably blue against orange, hence the lighter tone.
  const neutral = onBubble ? 'text-white/70' : 'text-muted-foreground';
  const seen = onBubble ? 'text-sky-200' : 'text-sky-500';

  const label =
    state === 'read'
      ? readAt
        ? t('receipts.read_at', { time: readAt, defaultValue: 'Read at {{time}}' })
        : t('receipts.read', { defaultValue: 'Read' })
      : state === 'delivered'
        ? t('receipts.delivered', { defaultValue: 'Delivered' })
        : t('receipts.sent', { defaultValue: 'Sent' });

  return (
    <span
      data-testid={`receipt-${state}`}
      role="img"
      aria-label={label}
      title={label}
      className={`ml-1 inline-flex items-center align-bottom ${state === 'read' ? seen : neutral}`}
    >
      {state === 'sent' ? (
        <Check size={13} aria-hidden="true" />
      ) : (
        <CheckCheck size={13} aria-hidden="true" />
      )}
    </span>
  );
};

/**
 * Collapse the message's own send state and the conversation's receipt cursors
 * into one indicator state.
 *
 * Send state wins: a message that never reached the server cannot be delivered
 * to anyone, whatever the conversation cursors say.
 */
export function receiptStateFor(
  sendState: 'sending' | 'failed' | undefined,
  tick: Tick,
): ReceiptState {
  if (sendState === 'failed') return 'failed';
  if (sendState === 'sending') return 'sending';
  return tick;
}
